import { DataTable, ExtendedColumnDef } from "@/components/Datatable";
import { IStoreDeposit } from "src/@types/store-deposit-maintenance";

import {
  formatAmount,
  formatChangedAt,
  formatOperationDate,
  text,
} from "../deposit-formatters";
import { EMPTY_CELL } from "../types";
import { DepositRowActions } from "./deposit-row-actions";

interface DepositTableProps {
  readonly deposits: IStoreDeposit[];
  readonly isLoading: boolean;
  readonly emptyMessage: string;
  readonly onEditNsu: (deposit: IStoreDeposit) => void;
  readonly onViewBankDetails: (deposit: IStoreDeposit) => void;
  readonly onEditAccount: (deposit: IStoreDeposit) => void;
}

const formatBank = (
  bankNumber: number | null,
  bankName: string | null,
): string => {
  if (bankNumber === null) return EMPTY_CELL;
  if (bankName === null) return String(bankNumber);
  return `${bankName} (${bankNumber})`;
};

const buildColumns = (
  props: DepositTableProps,
): ExtendedColumnDef<IStoreDeposit>[] => [
  {
    accessorKey: "operationDate",
    header: "Data",
    cell: ({ row }) => formatOperationDate(row.original.operationDate),
  },
  { accessorKey: "cashRegister", header: "Caixa" },
  { accessorKey: "operationNumber", header: "Nº Operação" },
  {
    accessorKey: "amount",
    header: "Valor",
    cell: ({ row }) => formatAmount(row.original.amount),
  },
  {
    accessorKey: "nsu",
    header: "NSU",
    cell: ({ row }) => (
      <span className="font-mono">{text(row.original.nsu)}</span>
    ),
  },
  {
    accessorKey: "bankNumber",
    header: "Banco",
    cell: ({ row }) => formatBank(row.original.bankNumber, row.original.bankName),
  },
  {
    accessorKey: "agency",
    header: "Agência",
    cell: ({ row }) => text(row.original.agency),
  },
  {
    accessorKey: "account",
    header: "Conta",
    cell: ({ row }) => text(row.original.account),
  },
  {
    accessorKey: "changedAt",
    header: "Última alteração",
    cell: ({ row }) =>
      formatChangedAt(row.original.changedAt, row.original.changedTime),
  },
  {
    accessorKey: "responsibleName",
    header: "Responsável",
    cell: ({ row }) => text(row.original.responsibleName),
  },
  {
    id: "actions",
    header: "Ações",
    cell: ({ row }) => (
      <div className="flex items-center">
        <DepositRowActions
          deposit={row.original}
          onEditNsu={props.onEditNsu}
          onViewBankDetails={props.onViewBankDetails}
          onEditAccount={props.onEditAccount}
        />
      </div>
    ),
  },
];

export function DepositTable(props: DepositTableProps) {
  const columns: ExtendedColumnDef<IStoreDeposit>[] = buildColumns(props);

  return (
    <div className="mt-4 overflow-x-auto">
      <DataTable
        columns={columns}
        data={props.deposits}
        isLoading={props.isLoading}
        emptyMessage={props.emptyMessage}
      />
    </div>
  );
}
